import { useTranslation } from '@/i18n/useTranslation';

export interface YesNoProps {
  readonly id: string;
  readonly question: string;
  readonly value: '' | 'yes' | 'no';
  readonly onChange: (value: 'yes' | 'no') => void;
  readonly error?: string;
  /**
   * Marks the pair as required — the frame draws an asterisk on both questions [12021:40603].
   *
   * Same contract as `Field`: the marker is a SIBLING of the legend, never inside it, so the group's
   * accessible name stays the question. There is no `required` attribute to set here — these are buttons,
   * not form controls — so the asterisk plus the `aria-invalid` below are the whole signal.
   */
  readonly required?: boolean;
}

/**
 * The two-button yes/no the frame draws for work authorisation and age [Figma · 12021:40566].
 *
 * A pair of buttons rather than a `<select>` or radios styled to look like buttons: the frame draws two
 * equal 218 x 44 controls, and on a screen somebody may be using standing up with cold hands, two 44px
 * targets beat a dropdown. `aria-pressed` carries the selection, so the state is not colour alone.
 */
export function YesNo({ id, question, value, onChange, error, required }: YesNoProps) {
  const t = useTranslation();

  /*
   * An INSET RING, not a border, for the reason `Button` carries the same note: a border sits outside the
   * box, so 44px of touch target plus 1px twice made each control 46 and every radio group 74 against a
   * drawn 72. Two pixels on a component that appears four times on Application. (Fit questions used it three
   * times until 03-us-kpw-3a; it now renders `ScreeningQuestionControl`, which copies these classes.)
   */
  const tone = (mine: 'yes' | 'no') =>
    value === mine
      ? 'ring-[var(--primary)] bg-[var(--primary-muted)] text-[var(--primary-muted-foreground)]'
      : 'ring-[var(--neutral-200)] bg-[var(--card)] text-[var(--muted-foreground)]';

  return (
    /*
      `role="group"` + `aria-labelledby`, not `<fieldset><legend>` — the same swap `DateOfBirthField`
      makes, for one more reason on top of its own.

      A `<legend>` must be its fieldset's FIRST child to name the group, and its text content IS that
      name — so the required asterisk can go neither inside it (which renamed the group to "…18 years of
      age or older?*", caught by the eleven-fields test) nor after it without leaving the row. Labelling a
      plain element instead puts the marker where the frame draws it and leaves the name exactly the
      question.
    */
    <div
      aria-labelledby={`${id}-question`}
      className="flex w-full flex-col gap-2"
      /*
        A hook, because `role="group"` is not specific enough to count on: the kiosk shell's language bar
        is a group too, so a query for the role picked it up as a twelfth field. Same purpose as
        `data-affix` on `Field` and `data-tone` on `Callout` — a stable handle for something the
        accessibility tree deliberately does not distinguish.
      */
      data-yesno=""
      role="group"
    >
      {/*
        NO `pb-2` here, and the question is 14/20 BOLD — both measured off the group's own instance
        [I12021:40633, read live 2026-09-08]: `Label + Check` is 20 tall, `yn` starts at y=28, and the
        whole group is 72.

        The padding was double-counting the 8px the container's `gap-2` already provides, so every group
        stood 28 + 8 + 44 = 80 against a drawn 72 — 8px each, four times on Application and (then) three on
        Fit questions, which put Fit's Continue 24px below where the frame drew it. Fit is rendered by
        `ScreeningQuestionControl` since 03-us-kpw-3a, which keeps this measured spacing. And the label
        took `text-base font-semibold` where the frame binds `Paragraph/S Bold` (SF Pro Text, 14px, 700,
        20 line-height): at 375 the extra two pixels wrapped the longer question onto a second line, which
        grew the group to 100 on the narrowest screen instead of shrinking it to 72.
      */}
      <div className="flex items-center gap-1">
        <span
          className="text-sm font-bold leading-5 text-[var(--foreground)]"
          id={`${id}-question`}
        >
          {question}
        </span>
        {required === true && (
          <span aria-hidden="true" className="text-sm leading-5 text-[var(--destructive)]">
            *
          </span>
        )}
      </div>
      <div className="flex w-full gap-3">
        {(['yes', 'no'] as const).map((choice) => (
          <button
            /*
              `aria-invalid` on the FIRST button of the pair, not on the fieldset.

              `useScrollToFirstError` finds the first offending control by querying
              `[aria-invalid="true"]` and then focuses it, so a group that only rendered a message was
              invisible to that query: a refused Continue whose only gaps were these two questions printed
              its errors and left the viewport where it was — on a form taller than the screen, exactly
              the "nothing happened" the scroll exists to prevent. The fieldset would satisfy the query
              but is not focusable, and this button is.
            */
            aria-invalid={error !== undefined && choice === 'yes' ? true : undefined}
            aria-pressed={value === choice}
            /*
              `--radius-sm` (8px) and a 2px ring in `--neutral-200`, with a 14/20 label — the frame's own
              values [12021:40603]. It was a 4px radius, a 1px `--border-subtle` ring and a 16px label,
              which read as a text input rather than as one of two choices.
            */
            className={`kiosk-focusable min-h-touch flex-1 rounded-[var(--radius-sm)] px-6 py-3 text-sm font-semibold leading-5 ring-2 ring-inset ${tone(choice)}`}
            id={`${id}-${choice}`}
            key={choice}
            onClick={() => {
              onChange(choice);
            }}
            type="button"
          >
            {t(choice === 'yes' ? 'application.yes' : 'application.no')}
          </button>
        ))}
      </div>
      {error !== undefined && (
        <span className="text-sm leading-5 text-[var(--error)]" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
